import { DepartmentLabel, useDepartmentText } from 'model/DepartmentTerminology';
import { Box, Group, LoadingOverlay, Menu, Stack, Tooltip, useMantineTheme } from '@mantine/core';
import { UserGetResponse } from 'api/actions/user-get/user-get-response';
import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Avatar from 'components/Avatar';
import P1Medium from 'components/typography/P1Medium';
import { IconDotsVertical, IconPencil, IconTrashX } from '@tabler/icons-react';
import { usePickUsers } from 'components/modals/pick-users-modal/PickUsersProvider';
import { OrganizationFormData } from 'components/forms/organization/OrganizationForm';
import { DepartmentFormData } from 'components/forms/department/DepartmentForm';
import { usePermissionDataProvider } from 'model/PermissionDataProvider';
import P3Regular from 'components/typography/P3Regular';
import { useConfirm } from 'components/modals/message/MessageProvider';
import Button from 'components/Button';
import ActionIcon from 'components/ActionIcon';
import { useResponsiveDesign } from 'hooks/use-responsive-design';
import {
  isPermissionEnabledForOrganization,
  OrganizationModuleFlags,
} from 'features/organization-modules/use-organization-modules';

type User = UserGetResponse;

interface PersonWithPermissionsProps {
  userId: number;
  permissions: number[];
  templateSlug: string;
  value: OrganizationFormData['people'] | DepartmentFormData['people'];
  updatePermissionsForPerson: ({ permissions, templateName }: { permissions: number[]; templateName: string }) => void;
  inForm: 'department' | 'organization';
  onUserDelete: (userId: number) => void;
  hideControls?: boolean;
  hideDelete?: boolean;
  compact?: boolean;
  summarizeInheritedPermissions?: boolean;
  permissionSources?: {
    key: string;
    label: string;
    permissions: number[];
    templateSlug: string;
  }[];
  moduleFlags: OrganizationModuleFlags;
}

/**
 * Renders the person's name, their permissions and allows to update the permissions.
 */
export default function PersonWithPermissions({
  userId,
  permissions,
  templateSlug,
  value: formValue,
  updatePermissionsForPerson,
  inForm,
  onUserDelete,
  hideControls = false,
  hideDelete = false,
  compact = false,
  summarizeInheritedPermissions = false,
  permissionSources,
  moduleFlags,
}: PersonWithPermissionsProps) {
  const departmentText = useDepartmentText();
  const { getAction } = useApi();
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const { confirm } = useConfirm();
  const { pickUsers } = usePickUsers();
  const { getPermission, getPermissionTemplate } = usePermissionDataProvider();
  const { isMobile } = useResponsiveDesign();

  const infoText = useMemo(() => {
    return inForm === 'organization' ? 'z organizácie' : departmentText('z položky úrovne', 'zo strediska');
  }, [departmentText, inForm]);
  const deleteActionLabel = inForm === 'department' ? 'Odstrániť priame oprávnenia' : `Zmazať používateľa ${infoText}`;
  const permissionSections = useMemo(() => {
    const displayedPermissionIds = new Set<number>();
    const sources = permissionSources ?? [
      {
        key: 'direct',
        label: 'Priame oprávnenia',
        permissions,
        templateSlug,
      },
    ];

    return sources.flatMap((source) => {
      const displayedPermissions = source.permissions.flatMap((permissionId) => {
        const permission = getPermission(permissionId);

        if (
          !permission ||
          displayedPermissionIds.has(permissionId) ||
          !isPermissionEnabledForOrganization(permission.slug, moduleFlags)
        ) {
          return [];
        }

        displayedPermissionIds.add(permissionId);
        return [permission];
      });

      if (displayedPermissions.length === 0) {
        return [];
      }

      const templateName = getPermissionTemplate(source.templateSlug)?.permissionTemplateName;

      return [
        {
          ...source,
          displayedPermissions,
          accessibleLabel: `${source.label}${templateName ? ` (${templateName})` : ''}`,
        },
      ];
    });
  }, [getPermission, getPermissionTemplate, moduleFlags, permissionSources, permissions, templateSlug]);
  const inheritedPermissions = useMemo(
    () =>
      Array.from(
        new Set(permissionSources?.filter(({ key }) => key !== 'direct').flatMap((source) => source.permissions) ?? [])
      ),
    [permissionSources]
  );
  const inheritedPermissionDetails = useMemo(
    () =>
      inheritedPermissions.flatMap((permissionId) => {
        const permission = getPermission(permissionId);
        return permission && isPermissionEnabledForOrganization(permission.slug, moduleFlags) ? permission : [];
      }),
    [getPermission, inheritedPermissions, moduleFlags]
  );
  const inheritanceDetails = useMemo(
    () =>
      permissionSources?.flatMap((source) => {
        if (source.key === 'direct') return [];

        const permissionNames = Array.from(
          new Set(
            source.permissions.flatMap((permissionId) => {
              const permission = getPermission(permissionId);
              return permission && isPermissionEnabledForOrganization(permission.slug, moduleFlags)
                ? [permission.permissionName]
                : [];
            })
          )
        );

        return permissionNames.length > 0 ? [{ key: source.key, label: source.label, permissionNames }] : [];
      }) ?? [],
    [getPermission, moduleFlags, permissionSources]
  );

  /**
   * Confirms the user's removal from the department/organization.
   */
  const confirmUserDelete = useCallback(() => {
    confirm({
      title: inForm === 'department' ? 'Odstrániť priame oprávnenia' : 'Zmazať používateľa',
      content:
        inForm === 'department' ? (
          <DepartmentLabel
            objectLabel="Naozaj chcete používateľovi odstrániť všetky priame oprávnenia v položke úrovne? Prípadné zdedené oprávnenia zostanú zachované."
            departmentLabel="Naozaj chcete používateľovi odstrániť všetky priame oprávnenia v stredisku? Prípadné zdedené oprávnenia zostanú zachované."
          />
        ) : (
          `Naozaj chcete zmazať používateľa ${infoText}?`
        ),
      onConfirm: () => onUserDelete(userId),
    });
  }, [confirm, inForm, infoText, onUserDelete, userId]);

  /**
   * Opens modal where the user's permissions can be updated and on the modal's close, updates the permissions.
   */
  const updateUsersPermissions = () => {
    pickUsers({
      title: '',
      permissionsTitle: `Oprávnenia pre: ${user?.firstName} ${user?.lastName}`,
      hiddenUsers: [], // The users are not displayed in the modal, so there are no hidden users.
      initialPermissions: permissions,
      inheritedPermissions,
      initialTemplateName: formValue[userId]?.templateName ?? templateSlug,
      moduleFlags,
      pickPermissions: true,
      pickUsers: false,
      inForm,
      onPick: ({ permissions, templateName }) => {
        updatePermissionsForPerson({ permissions, templateName });
      },
    });
  };

  useEffect(() => {
    const userGetAction = getAction('UserGet');

    userGetAction({ parameters: { userId: String(userId) } })
      .then((response) => setUser(response))
      .catch(panic)
      .finally(() => setLoading(false));
  }, [userId]);

  if (compact) {
    return (
      <Box pos="relative" mih={38}>
        {loading ? (
          <LoadingOverlay visible />
        ) : (
          <Tooltip
            label={<InheritanceTooltipDetails sources={inheritanceDetails} />}
            events={{ hover: true, focus: true, touch: true }}
            multiline
            withinPortal
          >
            <Group
              aria-label={`Zdedené oprávnenia používateľa ${user?.firstName} ${user?.lastName}`}
              bg="gray.0"
              px={10}
              py={6}
              spacing={8}
              tabIndex={0}
              noWrap
              sx={{ border: `1px solid ${theme.colors.gray[2]}`, borderRadius: 4, width: 'fit-content' }}
            >
              <Avatar fullName={`${user?.firstName} ${user?.lastName}`} size="m" bgColor={theme.colors.gray[7]} />
              <P3Regular color="gray.8">
                {user?.firstName} {user?.lastName}
              </P3Regular>
            </Group>
          </Tooltip>
        )}
      </Box>
    );
  }

  return (
    <Box aria-label={`Oprávnenia používateľa: ${permissionSections.map(({ label }) => label).join(', ')}`}>
      {loading ? (
        <LoadingOverlay visible={loading} />
      ) : (
        <>
          <Group
            style={{
              border: `1px solid ${theme.colors.gray[2]}`,
              borderTopLeftRadius: '4px',
              borderTopRightRadius: '4px',
              alignContent: 'center',
            }}
            bg="gray.0"
            py={12}
            px={16}
            position="apart"
            mih={56}
            spacing={16}
          >
            <Group spacing={12}>
              <Avatar fullName={`${user?.firstName} ${user?.lastName}`} size="m" bgColor={theme.colors.gray[7]} />
              <Group spacing={8} maw={380}>
                <P1Medium color="gray.8">
                  {user?.firstName} {user?.lastName}
                </P1Medium>
              </Group>
            </Group>
            {!hideControls && (
              <Group spacing={12}>
                <Button
                  variant="subtle-gray"
                  size="sm"
                  leftIcon={<IconPencil stroke="1.5" color={theme.colors.gray[7]} />}
                  onClick={updateUsersPermissions}
                >
                  Upraviť oprávnenia
                </Button>
                {!hideDelete && (
                  <>
                    <Box style={{ borderLeft: `1px solid ${theme.colors.gray[2]}` }} h={24}></Box>
                    <Menu shadow="md" width={300} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle-gray" size={isMobile ? 'sm' : 'md'} aria-label="Akcie používateľa">
                          <IconDotsVertical stroke="1.5" />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          color="red.8"
                          icon={<IconTrashX stroke={1.5} size={20} />}
                          onClick={confirmUserDelete}
                        >
                          {deleteActionLabel}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </>
                )}
              </Group>
            )}
          </Group>
          <Stack
            style={{
              borderBottom: `1px solid ${theme.colors.gray[2]}`,
              borderLeft: `1px solid ${theme.colors.gray[2]}`,
              borderRight: `1px solid ${theme.colors.gray[2]}`,
              borderBottomLeftRadius: '4px',
              borderBottomRightRadius: '4px',
            }}
            p={16}
            spacing={8}
          >
            {summarizeInheritedPermissions ? (
              <Group spacing={6}>
                {permissions.flatMap((permissionId) => {
                  const permission = getPermission(permissionId);

                  return permission && isPermissionEnabledForOrganization(permission.slug, moduleFlags) ? (
                    <PermissionPill key={permission.permissionId} permissionName={permission.permissionName} />
                  ) : (
                    []
                  );
                })}
                {inheritedPermissionDetails.length > 0 && (
                  <InheritedPermissionsBadge
                    permissionNames={inheritedPermissionDetails.map(({ permissionName }) => permissionName)}
                  />
                )}
              </Group>
            ) : (
              permissionSections.map(({ accessibleLabel, displayedPermissions, key }) => (
                <Stack key={key} spacing={4} role="group" aria-label={accessibleLabel}>
                  <P1Medium color="gray.9">{accessibleLabel}</P1Medium>
                  <Group spacing={6}>
                    {displayedPermissions.map((permission) => (
                      <PermissionPill key={permission.permissionId} permissionName={permission.permissionName} />
                    ))}
                  </Group>
                </Stack>
              ))
            )}
          </Stack>
        </>
      )}
    </Box>
  );
}

/** Renders one permission label. */
function PermissionPill({ permissionName }: { permissionName: string }) {
  const theme = useMantineTheme();

  return (
    <Box
      bg="gray.0"
      data-testid="permission-pill"
      style={{ padding: '0 5px' }}
      sx={{ border: `1px solid ${theme.colors.gray[2]}`, borderRadius: 999, width: 'fit-content' }}
    >
      <P3Regular color="gray.8">{permissionName}</P3Regular>
    </Box>
  );
}

/** Renders inherited permission names in a keyboard-accessible count badge. */
function InheritedPermissionsBadge({ permissionNames }: { permissionNames: string[] }) {
  const theme = useMantineTheme();

  return (
    <Tooltip
      label={<TooltipRows rows={permissionNames} />}
      events={{ hover: true, focus: true, touch: true }}
      multiline
      withinPortal
    >
      <Box
        aria-label={`Zdedené oprávnenia: ${permissionNames.join(', ')}`}
        bg="gray.0"
        data-testid="inherited-permissions-badge"
        px={5}
        tabIndex={0}
        sx={{ border: `1px solid ${theme.colors.gray[2]}`, borderRadius: 999, width: 'fit-content' }}
      >
        <P3Regular color="gray.8">(+{permissionNames.length})</P3Regular>
      </Box>
    </Tooltip>
  );
}

/** Renders tooltip content with one explanation per row. */
function TooltipRows({ rows }: { rows: string[] }) {
  return (
    <Stack spacing={2}>
      <P3Regular weight={600}>Zdedené oprávnenia</P3Regular>
      {rows.map((row) => (
        <P3Regular key={row}>{row}</P3Regular>
      ))}
    </Stack>
  );
}

/** Renders every inherited source path with the permissions contributed by that path. */
function InheritanceTooltipDetails({
  sources,
}: {
  sources: { key: string; label: string; permissionNames: string[] }[];
}) {
  return (
    <Stack spacing={6}>
      {sources.map(({ key, label, permissionNames }) => (
        <Stack key={key} spacing={2}>
          <P3Regular>{label}</P3Regular>
          {permissionNames.map((permissionName) => (
            <P3Regular key={permissionName}>• {permissionName}</P3Regular>
          ))}
        </Stack>
      ))}
    </Stack>
  );
}
